import { useState } from 'react';
import {
  Button,
  HeadingText,
  ParagraphText,
  StackItem,
  StackLayout,
  styled,
} from '@leagueplatform/genesis-core';
import { FormProvider, useForm } from '@leagueplatform/web-common';
import { captureAppError } from '@leagueplatform/observability';
import { TOAST_STATUS, toastActions } from '@leagueplatform/toast-messages';
import { useIntl } from '@leagueplatform/locales';
import { BasePage } from 'components/base-page.component';
import { TextCommunicationTermsModal } from 'components/modals/text-communication-terms/text-communication-terms.component';
import { RHFTextInput } from 'components/form-elements/rhf-text-input.component';
import { phoneNumberValidation } from 'common/utils/rhf-form-validation.util';
import { useUpdateAccountSetup } from 'hooks/use-update-account-setup/use-update-account-setup.hook';
import { SENTRY_CONSTANTS, APP_NAME } from 'common/constants/constants';
import { useIsBelowTablet } from 'hooks/use-is-below-tablet/use-is-below-tablet.hooks';
import { OnboardingMachineContext } from 'components/onboarding/onboarding.machine';
import { useSelector } from '@xstate/react';
import { ResponsiveButtonRow } from 'components/responsive-button-row/responsive-button-row.component';
import TextNotificationIllustration from 'assets/text-notifications-illustration.svg';
import {
  PRODUCT_AREA,
  trackAnalyticsEvent,
  usePageViewAnalytics,
  EVENT_NAME,
} from '@leagueplatform/analytics';
import { SCREEN_NAMES } from 'common/constants/analytics';

const Illustration = styled(`img`, {
  alignSelf: 'center',
});

const CONTENT_MAX_WIDTH = 327;

type TextNotificationValues = {
  mobilePhoneNumber?: string;
  isTextNotificationsEnabled?: boolean;
};

export const TextNotificationOptInView = () => {
  const { formatMessage } = useIntl();
  // TODO
  usePageViewAnalytics({
    product_area: PRODUCT_AREA.ONBOARDING,
    screen_name: SCREEN_NAMES.ENABLE_TEXT_NOTIFICATIONS,
  });

  const actor = OnboardingMachineContext.useActorRef();
  const { send } = actor;
  const { mobilePhoneNumber } = useSelector(
    actor,
    (machineSnapshot) => machineSnapshot.context,
  );

  const navigateToNextOboardingStep = () =>
    send({ type: 'navigateToAccountSetupCompleted' });

  const methods = useForm({
    defaultValues: {
      mobilePhoneNumber,
      isTextNotificationsEnabled: false,
    },
  });

  const { isSubmitting } = methods.formState;

  const { updateAccountSetup } = useUpdateAccountSetup();
  const isBelowTablet = useIsBelowTablet();

  const [
    isTextCommunicationTermsModalOpen,
    setIsTextCommunicationTermsModalOpen,
  ] = useState<boolean>(false);

  const handleMobilePhoneNumberChange = () => {
    if (methods.getValues('mobilePhoneNumber') !== mobilePhoneNumber) {
      methods.setValue('isTextNotificationsEnabled', false);
    }
  };

  const handleFormSubmit = async (values: TextNotificationValues) => {
    if (!values.isTextNotificationsEnabled) {
      setIsTextCommunicationTermsModalOpen(true);
    } else {
      trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
        product_area: PRODUCT_AREA.ONBOARDING,
        screen_name: SCREEN_NAMES.ENABLE_TEXT_NOTIFICATIONS,
        detail: 'enable texts',
      });

      try {
        await updateAccountSetup(values);
        navigateToNextOboardingStep();
      } catch (error) {
        let errorMessage;
        if (error instanceof Error) {
          captureAppError(error, {
            ownershipTeam: SENTRY_CONSTANTS.OWNERSHIP_TEAM,
            errorName: 'Error saving Text Notification Opt-In form',
            context: {
              Context: {
                description: formatMessage({ id: 'STR_ACCOUNT_SETUP' }),
              },
            },
          });
          if (error.message) {
            errorMessage = JSON.parse(error.message);
          }
        }
        toastActions.add({
          type: TOAST_STATUS.ERROR,
          text:
            errorMessage?.info?.reason ||
            formatMessage({ id: 'STR_SOMETHING_WENT_WRONG_TRY_AGAIN' }),
        });
      }
    }
  };

  return (
    <BasePage
      showNavBar={false}
      css={{
        padding: isBelowTablet ? '$none' : '$twoAndHalf',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        '.wrapper': {
          maxWidth: '708px',
          width: '100%',
        },
        ...(isBelowTablet && {
          '.wrapper': {
            width: 'auto',
          },
        }),
      }}
    >
      <StackItem
        css={{
          ...(isBelowTablet && {
            height: '100%',
          }),
          width: '100%',
          background: '$surfaceCardSecondary',
          boxShadow: '$card',
        }}
      >
        <StackLayout
          spacing="$two"
          css={{
            padding: '$two',
            paddingBlockEnd: '$none',
          }}
          horizontalAlignment="center"
        >
          <StackItem
            horizontalAlignment="center"
            css={{
              alignSelf: 'center',
              maxWidth: CONTENT_MAX_WIDTH,
              textAlign: 'center',
            }}
          >
            <Illustration
              src={TextNotificationIllustration}
              aria-hidden
              alt=""
              css={{
                marginBlockEnd: '$two',
              }}
            />
            <HeadingText size="xl" level="2" as="h1">
              {formatMessage(
                { id: 'STR_TEXT_NOTIFICATION_OPT_IN_HEADING' },
                {
                  appName: APP_NAME,
                },
              )}
            </HeadingText>
            <ParagraphText
              css={{
                marginBlockStart: '$half',
                color: '$onSurfaceTextSubdued',
              }}
            >
              {formatMessage({ id: 'STR_TEXT_NOTIFICATION_OPT_IN_COPY' })}
            </ParagraphText>
          </StackItem>
        </StackLayout>
        <FormProvider
          formMethods={methods}
          onSubmit={handleFormSubmit}
          css={{
            display: 'grid',
          }}
        >
          <StackItem
            css={{
              gap: '$one',
              minWidth: `${CONTENT_MAX_WIDTH}px`,
              maxWidth: CONTENT_MAX_WIDTH,
              marginInline: 'auto',
              marginBlock: '$two',
            }}
          >
            <RHFTextInput
              id="mobilePhoneNumber"
              type="tel"
              placeholder={formatMessage({
                id: 'STR_MOBILE_PHONE_NUMBER_PLACEHOLDER',
              })}
              label={formatMessage({
                id: 'STR_MOBILE_PHONE_NUMBER_LABEL',
              })}
              required
              requiredMessage={formatMessage({
                id: 'STR_MOBILE_PHONE_NUMBER_REQUIRED',
              })}
              registerOptions={phoneNumberValidation(
                formatMessage({ id: 'STR_PHONE_VALIDATION_ERROR' }),
              )}
              onChange={handleMobilePhoneNumberChange}
            />
          </StackItem>
          <StackItem
            css={{
              borderColor: '$decorativeBrandPrimaryBrightest',
              borderStyle: 'solid',
              borderBlockStartWidth: 'thin',
              paddingBlockStart: '$one',
              paddingBlockEnd: isBelowTablet ? '$five' : '$one',
              paddingInline: '$oneAndHalf',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              gap: '$one',
            }}
            horizontalAlignment="end"
          >
            <ResponsiveButtonRow
              primaryBtn={
                <Button
                  type="submit"
                  width={isBelowTablet ? 'fillContainer' : 'hugContents'}
                  css={{ flexGrow: 0 }}
                  loading={isSubmitting}
                >
                  {formatMessage({ id: 'STR_ALLOW_TEXT_NOTIFICATIONS' })}
                </Button>
              }
              secondaryBtn={
                <Button
                  width={isBelowTablet ? 'fillContainer' : 'hugContents'}
                  css={{ flexGrow: 0, backgroundColor: 'transparent' }}
                  priority="secondary"
                  quiet
                  destructive={false}
                  onClick={() => {
                    trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
                      product_area: PRODUCT_AREA.ONBOARDING,
                      screen_name: SCREEN_NAMES.ENABLE_TEXT_NOTIFICATIONS,
                      detail: "don't enable texts",
                    });
                    navigateToNextOboardingStep();
                  }}
                >
                  {formatMessage({ id: 'STR_NOT_NOW' })}
                </Button>
              }
            />
          </StackItem>
        </FormProvider>
      </StackItem>
      <TextCommunicationTermsModal
        open={isTextCommunicationTermsModalOpen}
        heading={formatMessage(
          { id: 'STR_TEXT_NOTIFICATION_TERMS' },
          { appName: APP_NAME },
        )}
        closeModal={(isTextCommunicationTermsAccepted: boolean) => {
          setIsTextCommunicationTermsModalOpen(false);
          if (
            isTextCommunicationTermsAccepted &&
            methods.getValues('mobilePhoneNumber')
          ) {
            // Only set the value if the user accepted terms and mobile number is not empty
            methods.setValue('isTextNotificationsEnabled', true);
            methods.handleSubmit(handleFormSubmit)();
          }
        }}
      />
    </BasePage>
  );
};
